import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import PartySocket from 'partysocket';
import type { PublicGame, PlateScoreEvent, GameEndedEvent, AppView } from '../types';
import type { ClientMessage, ServerMessage } from '../types/protocol';

const SESSION_KEY = 'plateSeeker_session';
const TOKEN_KEY = 'plateSeeker_sessionToken';
const PARTYKIT_HOST = import.meta.env.VITE_PARTYKIT_HOST;
const MAX_CODE_RETRIES = 5;

// localStorage persists reliably across page refreshes on all mobile browsers,
// unlike sessionStorage which iOS Safari can clear during a reload.
function getOrCreateSessionToken(): string {
  const stored = localStorage.getItem(TOKEN_KEY);
  if (stored) return stored;
  const token = typeof crypto?.randomUUID === 'function'
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36);
  localStorage.setItem(TOKEN_KEY, token);
  return token;
}

function generateCode(): string {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars[Math.floor(Math.random() * chars.length)];
  }
  return code;
}

function saveSession(gameCode: string): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify({ gameCode }));
}

function clearSession(): void {
  localStorage.removeItem(SESSION_KEY);
}

function httpHealthUrl(): string {
  const isLocal = PARTYKIT_HOST.startsWith('localhost') || PARTYKIT_HOST.startsWith('127.0.0.1');
  return `${isLocal ? 'http' : 'https'}://${PARTYKIT_HOST}/health`;
}

function send(ws: PartySocket, message: ClientMessage): void {
  ws.send(JSON.stringify(message));
}

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
}

interface GameContextValue {
  connected: boolean;
  view: AppView;
  game: PublicGame | null;
  myPlayerId: string;
  gameCode: string | null;
  lastScoreEvent: PlateScoreEvent | null;
  endEvent: GameEndedEvent | null;
  toasts: Toast[];
  dismissToast: (id: number) => void;
  createGame: (playerName: string) => void;
  joinGame: (code: string, playerName: string) => void;
  startGame: () => void;
  startDemo: (playerName: string) => void;
  updateLocation: (state: string, isGPS: boolean) => void;
  scorePlate: (plateState: string) => void;
  leaveGame: () => void;
}

const GameContext = createContext<GameContextValue | null>(null);

let toastCounter = 0;

export function GameProvider({ children }: { children: React.ReactNode }) {
  const socketRef = useRef<PartySocket | null>(null);
  const sessionToken = useRef(getOrCreateSessionToken());
  // A player's identity is now their sessionToken (stable across
  // reconnects), not a transport connection id, so this never changes.
  const myPlayerId = sessionToken.current;
  // Set by createGame/startDemo right before opening a fresh room; consumed
  // by the 'error' handler below to silently retry on a code collision
  // instead of surfacing it as a user-facing toast.
  const pendingRetryRef = useRef<(() => void) | null>(null);

  const [connected, setConnected] = useState(false);
  const [view, setView] = useState<AppView>('home');
  const [game, setGame] = useState<PublicGame | null>(null);
  const [gameCode, setGameCode] = useState<string | null>(null);
  const [lastScoreEvent, setLastScoreEvent] = useState<PlateScoreEvent | null>(null);
  const [endEvent, setEndEvent] = useState<GameEndedEvent | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((message: string, type: Toast['type'] = 'info') => {
    const id = ++toastCounter;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  }, []);

  const dismissToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const handleServerMessage = useCallback((ws: PartySocket, msg: ServerMessage) => {
    switch (msg.type) {
      case 'game_created': {
        pendingRetryRef.current = null;
        const { code, game: g } = msg.payload;
        saveSession(code);
        setGameCode(code);
        setGame(g);
        setView('lobby');
        break;
      }

      case 'game_joined': {
        const { code, game: g } = msg.payload;
        saveSession(code);
        setGameCode(code);
        setGame(g);
        setView('lobby');
        break;
      }

      case 'demo_started': {
        pendingRetryRef.current = null;
        const { code, game: g } = msg.payload;
        saveSession(code);
        setGameCode(code);
        setGame(g);
        setView('game');
        break;
      }

      case 'game_rejoined': {
        const { game: g, endEvent: ev } = msg.payload;
        saveSession(g.code);
        setGameCode(g.code);
        setGame(g);
        if (ev) setEndEvent(ev);
        const viewMap: Record<PublicGame['status'], AppView> = {
          waiting: 'lobby',
          playing: 'game',
          ended: 'end',
        };
        setView(viewMap[g.status]);
        break;
      }

      case 'rejoin_failed': {
        clearSession();
        break;
      }

      case 'player_joined': {
        const { player } = msg.payload;
        setGame(prev => prev ? { ...prev, players: [...prev.players, player] } : prev);
        addToast(`${player.name} joined the game!`, 'info');
        break;
      }

      case 'game_started': {
        setGame(msg.payload.game);
        setView('game');
        break;
      }

      case 'location_updated': {
        const { playerId, state, isGPS } = msg.payload;
        setGame(prev =>
          prev
            ? {
                ...prev,
                players: prev.players.map(p =>
                  p.id === playerId ? { ...p, currentState: state, isGPS } : p
                ),
              }
            : prev
        );
        break;
      }

      case 'plate_scored': {
        const event = msg.payload;
        setLastScoreEvent(event);
        setGame(prev =>
          prev
            ? {
                ...prev,
                players: prev.players.map(p =>
                  p.id === event.playerId ? { ...p, score: event.newScore } : p
                ),
              }
            : prev
        );
        addToast(`${event.playerName} scored +${event.points} (${event.plateStateName})`, 'success');
        break;
      }

      case 'game_ended': {
        const event = msg.payload;
        setEndEvent(event);
        setGame(prev => prev ? { ...prev, status: 'ended', winner: event.winnerId } : prev);
        setView('end');
        break;
      }

      case 'player_left': {
        const { playerId } = msg.payload;
        setGame(prev => {
          if (!prev) return prev;
          const leaving = prev.players.find(p => p.id === playerId);
          if (leaving) addToast(`${leaving.name} left the game.`, 'info');
          return { ...prev, players: prev.players.filter(p => p.id !== playerId) };
        });
        break;
      }

      case 'error': {
        if (msg.payload.code === 'code_taken' && pendingRetryRef.current) {
          const retry = pendingRetryRef.current;
          pendingRetryRef.current = null;
          ws.close();
          retry();
          break;
        }
        addToast(msg.payload.message, 'error');
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Opens a PartySocket for `code`. `onFirstOpen`, when given, fires once
  // on the socket's first successful open (used to send the action that
  // established this connection: create/join/demo). Every open after that
  // — including the very first one when `onFirstOpen` is omitted, which is
  // how mount-time session resume works — sends `rejoin_game`, so a
  // network-level auto-reconnect (partysocket reconnects on its own) or a
  // forced reconnect (BFCache restore, tab visibility) re-establishes this
  // connection's player identity the same way a fresh resume does.
  const connectToRoom = useCallback((code: string, onFirstOpen?: (ws: PartySocket) => void): PartySocket => {
    socketRef.current?.close();

    const ws = new PartySocket({ host: PARTYKIT_HOST, room: code, party: 'game-room' });
    let sentInitial = false;

    ws.addEventListener('open', () => {
      setConnected(true);
      if (onFirstOpen && !sentInitial) {
        sentInitial = true;
        onFirstOpen(ws);
      } else {
        send(ws, { type: 'rejoin_game', payload: { sessionToken: sessionToken.current } });
      }
    });

    ws.addEventListener('close', () => setConnected(false));

    ws.addEventListener('message', (event: MessageEvent) => {
      try {
        const msg = JSON.parse(event.data) as ServerMessage;
        handleServerMessage(ws, msg);
      } catch {
        // ignore malformed frames
      }
    });

    socketRef.current = ws;
    return ws;
  }, [handleServerMessage]);

  // A dummy /health check just to know the Worker is reachable — there's
  // no free "ambient" connection to hang this off anymore, since every
  // PartySocket is tied to one specific game room from the moment it's
  // constructed (unlike the old single persistent Socket.io connection).
  // Re-run after leaveGame() closes the room socket (which flips `connected`
  // to false via the 'close' listener) so the home screen recovers instead
  // of being stuck on "Connecting to server…" until a hard refresh.
  const checkHealth = useCallback(() => {
    fetch(httpHealthUrl())
      .then(res => { if (res.ok) setConnected(true); })
      .catch(() => { /* HomePage shows "Connecting to server…" until this resolves */ });
  }, []);

  useEffect(() => {
    checkHealth();

    const raw = localStorage.getItem(SESSION_KEY);
    if (raw) {
      try {
        const { gameCode: storedCode } = JSON.parse(raw) as { gameCode: string };
        if (storedCode) connectToRoom(storedCode);
      } catch {
        clearSession();
      }
    }

    // iOS Safari can restore a frozen page from the back-forward cache
    // (BFCache) without re-running effects or firing a normal load. When
    // that happens the underlying WebSocket has been closed by the
    // browser. Force a reconnect so the room's 'open' handler fires
    // immediately instead of waiting on partysocket's own retry backoff.
    function handlePageShow(event: PageTransitionEvent) {
      if (event.persisted) socketRef.current?.reconnect();
    }

    // Belt-and-suspenders for phone lock/unlock or tab switch: if the
    // socket dropped while the page was hidden, reconnect as soon as it
    // becomes visible.
    function handleVisibilityChange() {
      if (document.visibilityState === 'visible' && socketRef.current?.readyState !== WebSocket.OPEN) {
        socketRef.current?.reconnect();
      }
    }

    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      socketRef.current?.close();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const attemptCreate = useCallback((playerName: string, retryCount: number) => {
    if (retryCount >= MAX_CODE_RETRIES) {
      addToast('Could not create a game right now. Please try again.', 'error');
      return;
    }
    const code = generateCode();
    pendingRetryRef.current = () => attemptCreate(playerName, retryCount + 1);
    connectToRoom(code, ws => send(ws, {
      type: 'create_game',
      payload: { playerName, sessionToken: sessionToken.current },
    }));
  }, [connectToRoom, addToast]);

  const attemptDemo = useCallback((playerName: string, retryCount: number) => {
    if (retryCount >= MAX_CODE_RETRIES) {
      addToast('Could not start the demo right now. Please try again.', 'error');
      return;
    }
    const code = generateCode();
    pendingRetryRef.current = () => attemptDemo(playerName, retryCount + 1);
    connectToRoom(code, ws => send(ws, {
      type: 'start_demo',
      payload: { playerName, sessionToken: sessionToken.current },
    }));
  }, [connectToRoom, addToast]);

  const createGame = useCallback((playerName: string) => {
    attemptCreate(playerName, 0);
  }, [attemptCreate]);

  const joinGame = useCallback((code: string, playerName: string) => {
    const upper = code.toUpperCase();
    connectToRoom(upper, ws => send(ws, {
      type: 'join_game',
      payload: { code: upper, playerName, sessionToken: sessionToken.current },
    }));
  }, [connectToRoom]);

  const startGame = useCallback(() => {
    if (socketRef.current) send(socketRef.current, { type: 'start_game', payload: {} });
  }, []);

  const startDemo = useCallback((playerName: string) => {
    attemptDemo(playerName, 0);
  }, [attemptDemo]);

  const updateLocation = useCallback((state: string, isGPS: boolean) => {
    if (socketRef.current) send(socketRef.current, { type: 'update_location', payload: { state, isGPS } });
  }, []);

  const scorePlate = useCallback((plateState: string) => {
    if (socketRef.current) send(socketRef.current, { type: 'score_plate', payload: { plateState } });
  }, []);

  const leaveGame = useCallback(() => {
    clearSession();
    if (socketRef.current) {
      send(socketRef.current, { type: 'leave_game', payload: {} });
      socketRef.current.close();
      socketRef.current = null;
      checkHealth();
    }
    setGame(null);
    setGameCode(null);
    setLastScoreEvent(null);
    setEndEvent(null);
    setView('home');
  }, [checkHealth]);

  return (
    <GameContext.Provider
      value={{
        connected,
        view,
        game,
        myPlayerId,
        gameCode,
        lastScoreEvent,
        endEvent,
        toasts,
        dismissToast,
        createGame,
        joinGame,
        startGame,
        startDemo,
        updateLocation,
        scorePlate,
        leaveGame,
      }}
    >
      {children}
    </GameContext.Provider>
  );
}

export function useGame() {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error('useGame must be used inside GameProvider');
  return ctx;
}
